import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, X, Camera, Info, Check, Users, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useDropzone } from 'react-dropzone';
import Image from 'next/image';
import { useWakeLock } from '@/utils/wakeLock';
import { PrivacyBadge } from '@/components/common/PrivacyBadge';

interface PhotoOnlyUploadStepProps {
  photoUrl?: string;
  onPhotoChange: (url: string) => void;
  onUploadComplete?: () => void;
  onUploadError?: (error: string) => void;
  storyType?: 'single' | 'multi';
  onAutoAdvance?: () => void;
  onAnalysisComplete?: (analysis: any) => void;
}

export function PhotoOnlyUploadStep({
  photoUrl,
  onPhotoChange,
  onUploadComplete,
  onUploadError,
  storyType = 'single',
  onAutoAdvance,
  onAnalysisComplete
}: PhotoOnlyUploadStepProps) {
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Wake lock hook
  const { requestWakeLock, releaseWakeLock } = useWakeLock();

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      console.log('[PhotoOnlyUploadStep] onDrop triggered with files:', acceptedFiles.length);
      setError(null);
      setIsUploading(true);
      
      // For single character, explicitly enforce only 1 file
      if (storyType === 'single' && acceptedFiles.length > 1) {
        setError('Please select only one photo for a single character story');
        setIsUploading(false);
        return;
      }
      
      // Validate file types
      const validFiles = acceptedFiles.filter(
        file => file.type === 'image/jpeg' || file.type === 'image/png'
      );
      
      if (validFiles.length !== acceptedFiles.length) {
        setError('Only JPG and PNG images are accepted');
        setIsUploading(false);
        return;
      }
      
      // For single character, limit to 1 photo
      const file = validFiles[0];
      
      try {
        // Request wake lock during upload
        await requestWakeLock('Photo upload');
        
        // Upload file
        const formData = new FormData();
        formData.append('file', file);
        
        console.log('[PhotoOnlyUploadStep] Uploading file:', file.name, 'Size:', file.size);
        
        const response = await fetch('/api/supabase-upload?bucket=child-photos&folder=childPhotoUploader', {
          method: 'POST',
          body: formData
        });
        
        console.log('[PhotoOnlyUploadStep] Upload response status:', response.status);
        
        if (!response.ok) {
          throw new Error('Upload failed');
        }
        
        const data = await response.json();
        const serverUrl = data.url;
        
        // Update state with server URL
        onPhotoChange(serverUrl);
        if (onUploadComplete) onUploadComplete();
        
        // Trigger AI analysis for single character stories
        if (storyType === 'single' && onAnalysisComplete) {
          console.log('[PhotoOnlyUploadStep] Starting AI analysis for photo:', serverUrl);
          try {
            const analysisResponse = await fetch('/api/analyze-photo', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ photoUrl: serverUrl })
            });
            
            if (analysisResponse.ok) {
              const analysis = await analysisResponse.json();
              console.log('[PhotoOnlyUploadStep] AI Analysis result:', analysis);
              onAnalysisComplete(analysis);
            }
          } catch (error) {
            console.error('[PhotoOnlyUploadStep] Error analyzing photo:', error);
            // Non-blocking - continue even if analysis fails
          }
        }
        
        // Auto-advance after a brief success message
        setTimeout(() => {
          if (onAutoAdvance) {
            console.log('[PhotoOnlyUploadStep] Auto-advancing to next step');
            onAutoAdvance();
          }
        }, 1500); // Show success for 1.5 seconds then advance
        
      } catch (error) {
        console.error('Error uploading photo:', error);
        setError('Failed to upload photo. Please try again.');
        if (onUploadError) onUploadError('Upload failed');
      } finally {
        setIsUploading(false);
        // Release wake lock
        await releaseWakeLock();
      }
    },
    [onPhotoChange, onUploadComplete, onUploadError, storyType, onAutoAdvance, requestWakeLock, releaseWakeLock]
  );

  const removePhoto = () => {
    onPhotoChange('');
    setError(null);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    maxSize: 5 * 1024 * 1024, // 5MB
    maxFiles: 1,
    multiple: false,
    disabled: isUploading || !!photoUrl, // Disable if uploading or photo already uploaded
    onDropAccepted: (files) => console.log('[PhotoOnlyUploadStep] Files accepted:', files.length),
    onDropRejected: (rejections) => {
      console.log('[PhotoOnlyUploadStep] Files rejected:', rejections);
      if (rejections.some(r => r.errors.some(e => e.code === 'too-many-files'))) {
        setError('Please select only one photo');
      } else if (rejections.some(r => r.errors.some(e => e.code === 'file-too-large'))) {
        setError('Photo size must be less than 5MB');
      }
    },
    onError: (err) => console.error('[PhotoOnlyUploadStep] Dropzone error:', err)
  });

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      {/* Privacy Badge - Always visible at top */}
      <PrivacyBadge variant="compact" />
      
      {/* Main upload area */}
      {!photoUrl ? (
        <div
          {...getRootProps()}
          className={`border-3 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all ${
            isDragActive 
              ? 'border-purple-500 bg-purple-50 scale-105' 
              : isUploading
              ? 'border-gray-300 bg-gray-50 cursor-not-allowed opacity-75'
              : 'border-purple-300 hover:border-purple-500 hover:bg-purple-50/50'
          }`}
        >
          <input {...getInputProps()} />
          
          {isUploading ? (
            <div className="flex flex-col items-center justify-center">
              <Loader2 className="w-16 h-16 text-purple-600 animate-spin mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                Uploading your photo...
              </h3>
              <p className="text-gray-500">
                This will just take a moment
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center">
              <div className="w-24 h-24 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mb-6">
                <Camera className="text-purple-600" size={40} />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-3">
                {isDragActive ? 'Drop your photo here!' : 'Upload a Photo'}
              </h3>
              <p className="text-gray-600 mb-2 text-lg">
                {storyType === 'multi' 
                  ? 'Upload a clear group photo with 1-5 people'
                  : 'Upload a clear photo of your child'}
              </p>
              <p className="text-sm text-gray-500 mb-6">
                We'll analyze the photo to personalize the story
              </p>
              <button 
                type="button"
                className="bg-purple-600 text-white px-8 py-3 rounded-full hover:bg-purple-700 transition shadow-lg text-lg font-medium"
              >
                Choose Photo
              </button>
              <p className="text-xs text-gray-400 mt-4">
                JPEG or PNG • Max 5MB • {storyType === 'multi' ? 'Group photo' : 'Single photo'}
              </p>
            </div>
          )}
        </div>
      ) : (
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="relative"
        >
          <div className="relative rounded-2xl overflow-hidden h-80 shadow-xl bg-gray-50">
            <Image
              src={photoUrl}
              alt="Uploaded photo"
              fill
              style={{ objectFit: 'contain' }}
              priority
            />
            {!isUploading && (
              <button
                type="button"
                onClick={removePhoto}
                className="absolute top-3 right-3 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-colors shadow-lg"
                aria-label="Remove photo"
              >
                <X size={20} />
              </button>
            )}
          </div>
          
          {/* Success message */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-6 flex items-center justify-center"
          >
            <div className="bg-green-50 border-2 border-green-200 rounded-full px-6 py-3 flex items-center gap-3">
              <div className="bg-green-500 rounded-full p-1">
                <Check className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-green-800 text-lg">
                Photo uploaded successfully!
              </span>
            </div>
          </motion.div>
          
          {/* Auto-advance notice */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-4 text-center"
          >
            <p className="text-sm text-gray-500">
              Moving to the next step...
            </p>
          </motion.div>
        </motion.div>
      )}

      {/* Error display */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg"
        >
          <p className="font-medium">{error}</p>
        </motion.div>
      )}

      {/* Photo tips - shown when no photo */}
      {!photoUrl && !isUploading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-blue-50 border border-blue-200 rounded-lg p-4"
        >
          <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
            <Camera className="w-4 h-4 mr-2" />
            Photo Tips for Best Results
          </h4>
          <ul className="space-y-1 text-sm text-blue-800">
            {storyType === 'multi' ? (
              <>
                <li>• Include 1-5 people clearly visible</li>
                <li>• Faces should be well-lit and unobstructed</li>
                <li>• Group should be close together</li>
                <li>• Avoid shadows on faces</li>
              </>
            ) : (
              <>
                <li>• Use good lighting (natural light works best)</li>
                <li>• Face should be clearly visible</li>
                <li>• Plain background works best</li>
                <li>• Recent photo recommended</li>
              </>
            )}
          </ul>
        </motion.div>
      )}

      {/* Simplified disclaimer toggle */}
      <div className="flex justify-center">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setShowDisclaimer(!showDisclaimer)}
          className="text-gray-500 hover:text-gray-700"
        >
          <Info className="w-4 h-4 mr-1" />
          About photo usage
        </Button>
      </div>
      
      {/* Expandable disclaimer */}
      <AnimatePresence>
        {showDisclaimer && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm">
              <h4 className="font-medium text-amber-800 mb-2">
                How We Use Your Photo
              </h4>
              <ul className="space-y-1 text-amber-700">
                <li>• We analyze the photo to detect features</li>
                <li>• We create an artistic interpretation, not an exact copy</li>
                <li>• Your photo is stored securely and never shared</li>
                <li>• You can preview the result before purchasing</li>
              </ul>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
